import { HttpClient, HttpParams } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { ILocationService } from 'angular';
import { LogBookRootScopeService, PagedEntity } from 'app/shared/model/app.model';
import { GeneralParameters, TemplateProjections } from "app/shared/model/request-parameter.model";
import { IRouteService } from 'app/shared/model/route.model';
import { OptionType } from "ng/src/app/core/Option";
import { TimezoneModel } from "ng/src/app/log-settings/timezone.service";
import { Template, TemplateWithUsers } from 'ng/src/app/templates/shared/template.model';
import { UserService } from 'ng/src/app/users/shared/user.service';
import { BehaviorSubject, defer, forkJoin, Observable, of, Subject } from 'rxjs';
import { catchError, filter, map, mergeMap, scan, shareReplay, startWith, switchMap, tap } from 'rxjs/operators';
import { Utils } from '../core/utils';
import { NoteService } from '../notes/shared/note.service';
import { HALResponse } from '../shared/model/app.model';
import { TemplateService } from "../templates/shared/template.service";
import { LogInvitation, LogInvitationResponse } from './log-list/log-list.model';
import { DataType, Log, LogConfig, LogQuery, LogRole, LogWithData, LogWithTemplates } from './log.model';

@Injectable({
	providedIn: 'root'
})
export class LogService {
	private logChangeStart = new Subject<() => void>();
	private _logInvitationIds: number[] = [];
	private logRoles$?: Observable<LogRole[]>;
	private pendingLogInvitations = new Subject<boolean>();
	
	private get logInvitationIds() {
		return this._logInvitationIds;
	}
	private set logInvitationIds(ids) {
		this.pendingLogInvitations.next(ids.length > 0);
		this._logInvitationIds = ids;
	}

	private static _selectedLog = new BehaviorSubject<LogWithData | undefined>(undefined);
	private static _selectedLogTemplates: Observable<ReadonlyArray<Readonly<TemplateWithUsers>>>;

	static get isReadOnlyUser() {
		return (this.selectedLog?.role ?? 'READ') == 'READ';
	}

	static get selectedLog(): LogWithData | undefined {
		return this._selectedLog.getValue();
	}
	
	static set selectedLog(log) {
		this._selectedLog.next(log);
	}
	
	/**
	 * @deprecated
	 */
	static set templates(templates: Template[]) {
		templates.sort(Utils.createSortFunction(t => t.name.toLowerCase()));
		this.refreshTemplates();
	}
	
	static getSelectedLogHomePage() {
		return (this.selectedLog as LogWithData)?.logConfigs?.defaultLogPage ?? '/notes';
	}
	
	static navigateToSelectedLogHomePage(location: ILocationService) {
		return location.url(this.getSelectedLogHomePage()).search('log', this.selectedLog?.id ?? null);
	}
	
	get selectedLog() {
		return LogService.selectedLog;
	}
	
	set selectedLog(val) {
		LogService.selectedLog = val;
	}
	
	logChangeStart$ = this.logChangeStart.asObservable();
	pendingLogInvitations$ = this.pendingLogInvitations.asObservable();
	static get onTemplatesChanged$() {
		return LogService._selectedLogTemplates;
	};
	
	static readonly selectedLog$ = LogService._selectedLog.asObservable();
	
	private static refreshTemplatesSubject = new Subject<void>();
	
	static refreshTemplates() {
		this.refreshTemplatesSubject.next();
	}
	
	constructor(
		private http: HttpClient,
		private noteService: NoteService,
		private userService: UserService,
		private templateService: TemplateService,
		@Inject('$location') private $location: ILocationService,
		@Inject('$rootScope') private $rootScope: LogBookRootScopeService,
		@Inject('$route') private $route: IRouteService
	) {
		
		// When None, it means the templates have not been fetched yet.
		let fetchingDate: OptionType<Date | null> = OptionType.none();
		LogService._selectedLogTemplates = LogService.selectedLog$.pipe(
			(source) => {
				return source.pipe(
					tap(() => fetchingDate = OptionType.none()),
					map((logData) => logData?.id ?? null),
				)
			},
			switchMap((logId) => {
				return LogService.refreshTemplatesSubject.pipe(
					filter(() => {
						const now = new Date();
						// If fetchingDate is None, it means the templates have not been fetched yet.
						// If it is Some, we only allow fetches from earlier dates.
						return fetchingDate
							.filter((dt) => dt == null || dt < now)
							.isSome();
					}),
					startWith(null),
					map(() => logId),
				)
			}),
			(source) => {
				let cached = [] as TemplateWithUsers[];
				
				return source.pipe(
					switchMap((logId) => {
						if (logId == null) {
							return of([]);
						}
						
						const dtOption = fetchingDate.nonNullable().match({
							some: (dt) => ({ updatedAfter: dt }),
							none: () => {},
						});
						
						return templateService.queryTemplateCache({
							logId: logId,
							ids: cached.map(t => t.id),
							...dtOption,
						}).pipe(
							catchError((err) => {
								if (fetchingDate.isSome()) {
									fetchingDate = OptionType.of(null);
								}
								
								console.error(err);
								return of([]);
							}),
							tap(() => fetchingDate = OptionType.of(new Date())),
							map((results) => {
								if (Array.isArray(results)) {
									return results;
								}
								
								const merge = !!(dtOption && dtOption.updatedAfter);
								const newTemplates = results.templates;
								if (merge) {
									const templateMap = Utils.arrayToMap(cached, t => [t.id, t]);
									for (const t of newTemplates) {
										templateMap[t.id] = t;
									}
									for (const remove of results.removedIds) {
										delete templateMap[remove];
									}
									
									cached = Object.values(templateMap);
								}
								else {
									cached = newTemplates;
								}
								
								cached.sort(Utils.createSortFunction(t => t.name.toLowerCase()));
								return cached;
							})
						);
					})
				);
			},
			catchError((err) => {
				console.error(err);
				return [];
			}),
			map((templates) => {
				const selfUrl = window.location.origin + '/api/templates';
				return Object.freeze(
					templates.map((t) => {
						if (!Object.isFrozen(t)) {
							t._links = {
								self: {
									href: `${selfUrl}/${t.id}`,
								}
							} as any;
						}
						
						return Object.freeze(t);
					})
				);
			}),
			shareReplay(),
			(source) => {
				// On new subscriptions, we check for updates templates.
				return new Observable(subscriber => {
					if (fetchingDate.isSome()) {
						LogService.refreshTemplates();
					}
					
					return source.subscribe(subscriber);
				});
			}
		);
	}
	
	selectLog(log: Log | number | null, callback?: Function) {
		const changeLog = () => {
			LogService.selectedLog = undefined;
			delete this.$rootScope.selectedLog;
				
			this.$location.search('log', typeof log == 'number' ? log : log ? log.id : null);
			
			if (log)
				this.$route.reload();
			else if (callback)
				callback();
			else
				throw 'Provide a callback function to execute after deselecting log';
		}
		
		if (this.logChangeStart.observers.length)
			this.logChangeStart.next(changeLog);
		else
			changeLog();
	}
	
	getLogs<T extends Log[] = Log[]>(withRecentNotes = false, projection = '') {
		const getLogs$ = this.http.get<HALResponse<{ logs: T }>>(
			'api/logs?size=1000000' + (projection ? '&projection=' + projection : '')
		);

		return forkJoin({
			logs: getLogs$,
			recentNotes: withRecentNotes ? this.noteService.getRecentNotes() : of(null)
		})
		.pipe(
			map(r => {
				const logs = r.logs._embedded.logs;

				if (r.recentNotes) {
					r.recentNotes.forEach(note => {
						logs.find(log => log.id == note.logId)!.lastNote = note;
					});
				}
				
				return logs;
			})
		);
	}
	
	fetch(query: LogQuery) {
		return this.http.get<HALResponse<{ logs: Log[] }>>('/api/logs', {
			params: query
		})
			.pipe(
				map(response => PagedEntity.from(response._embedded.logs, response.page))
			);
	}

	getLogsWithTemplates(logId?: number): Observable<LogWithTemplates[]>{
		let params: GeneralParameters = {
			size: 10000
		};
		if (typeof logId === 'number')
			params.id = logId;

		return forkJoin({
			logs: this.http.get<HALResponse<{ logs: LogWithTemplates[]}>>('/api/logs', { params: params }),
			templates: this.templateService.getTemplates(logId, TemplateProjections.WITH_USERS)
		}).pipe(
			map(r =>
				r.logs._embedded.logs.map(log => ({
					...log,
					templates: r.templates.filter(template => template.logId === log.id).sort(Utils.createSortFunction(t => t.name))
				})
			)
		));
	}

	getLogById(logId: number) {
		return this.http.get<Log>(`api/logs/${logId}`, {
			params: {
				projection: 'logProjection'
			}
		});
	}
	
	getLogRoles() {
		if (!this.logRoles$) {
			this.logRoles$ = this.http.get<HALResponse<{ logRoles: LogRole[] }>>(
				'api/logRoles'
			)
			.pipe(
				map(r => r._embedded.logRoles),	
				shareReplay(1)
			);
		}
		
		return this.logRoles$;
	}

	getLogInvitations() {
		return forkJoin({
			byUser: this.http.get<HALResponse<{ logInvitations: LogInvitation[] }>>(
				`api/logInvitations?user=${this.$rootScope.user!.id}`
			),
			byEmail: this.userService.getLogBookUserEmails(undefined, [this.$rootScope.user!.id])
				.pipe(
					mergeMap(emails => {
						if (!emails.length) return of([]);
						
						let params = new HttpParams();
						emails.forEach(e => params = params.append('email', e.email));

						return this.http.get<HALResponse<{ logInvitations: LogInvitation[] }>>(
							`api/logInvitations`, {
								params: params
							})
							.pipe(
								map(res => res._embedded.logInvitations)
							)
					})
				)
		})
		.pipe(
			map(r => [
				...r.byUser._embedded.logInvitations,
				...r.byEmail
			]),
			tap(logInvitations => this.logInvitationIds = logInvitations.map(li => li.id))
		);
	}

	respondToLogInvitation(logInvitationId: number, accepted: boolean) {
		return this.http.patch<LogInvitationResponse>(`api/logInvitations/${logInvitationId}`, {
			accepted: accepted
		})
		.pipe(
			tap(() => this.logInvitationIds = this.logInvitationIds.filter(id => id != logInvitationId))
		);
	}

	static getConfigValueFor(key: string) {
		return this.selectedLog?.logConfigs[key];
	}
	
	
	getLogConfigValues(key?: string | { type: 'key' | 'keyLike', key: string }, logId?: number, hierarchy: null | string = 'logbook.webui.config'): Observable<LogConfig[]> {
		let params = new HttpParams();
		
		if (hierarchy !== null) {
			params = params.set('hierarchy', hierarchy);
		}
		
		if (logId)
			params = params.set('logId', logId.toString());
		
		if (key) {
			if (typeof key === 'string')
				params = params.set('key', key);
			else
				params = params.set(key.type, key.key);
		}
		
		return this.http.get<HALResponse<{ logConfigs: LogConfig[] }>>(
			'api/logConfigs', { params: params }
		)
		.pipe(
			map(r => r._embedded.logConfigs)	
		);
	}
	
	patchLogConfig(id: number, config: Partial<Pick<LogConfig, 'key' | 'hierarchy' | 'value'>>) {
		const toSend = Object.entries(config).reduce((acc, [key, value]) => {
			if (value !== undefined) {
				acc[key as keyof typeof config] = value;
			}
			
			return acc;
		}, {} as typeof config);
		
		return this.http.patch(`/api/logConfigs/${id}`, toSend);
	}
	
	postLogConfig(config: Pick<LogConfig, 'key' | 'hierarchy' | 'value' | 'logId'>, dataType: DataType) {
		const type = DataType.toURI(dataType);
		const toSend = {
			...config,
			log: `/api/logs/${config.logId}`,
			dataType: type
		}
		
		return this.http.post('/api/logConfigs', toSend);
	}
	
	deleteLogConfig(id: number) {
		return this.http.delete<unknown>(`/api/logConfigs/${id}`);
	}
	
	saveConfigValue(config: Pick<LogConfig, 'key' | 'hierarchy' | 'value' | 'logId'>, dataType: DataType) {
		const type = DataType.toURI(dataType);
		return this.getLogConfigValues(config.key, config.logId).pipe(
			switchMap(result => {
				const patchUrl = result[0]?._links.self.href;
				const toSend = {
					...config,
					log: `/api/logs/${config.logId}`,
					dataType: type
				}
				return patchUrl ? this.http.patch(patchUrl, toSend) : this.http.post(`/api/logConfigs`, toSend);
			}),
			switchMap(() => this.updateLogConfigs())
		)
	}
	
	updateLogConfigs(): Observable<Record<string, string>> {
		if (!LogService.selectedLog) return of({});
		const logId = LogService.selectedLog.id;
		return this.http.get<HALResponse<{ logConfigs: LogConfig[] }>>('/api/logConfigs', {
			params: {
				log: logId
			} as GeneralParameters
		})
			.pipe(
				map((results) => {
					const mapped = Utils.arrayToMap<LogConfig, string>(results._embedded.logConfigs, config => [config.key, config.value]);
					if (logId === LogService.selectedLog?.id)
						LogService.selectedLog.logConfigs = mapped;
						
					return mapped;
				})
			)
		
	}
	
	updateLog(log: Log, ...filterFields: (keyof Log)[]) {
		return this.fetch({ id: log.id })
			.pipe(
				map(res => {
					const newLog = res[0];
					const fields: (keyof Log)[] = Array.isArray(filterFields) ? filterFields : Object.keys(newLog) as any;
					
					fields.forEach(field => {
						(log as any)[field] = (newLog as any)[field];
					});
					
					return log;
				})
			);
	}
	
	updateLogTimeZone(timezone: TimezoneModel, log: Log) {
		return this.http.patch(`/api/logs/${log.id}`, {timeZone: timezone.id});
	}
	
	navigateToLogHomePage() {
		return LogService.navigateToSelectedLogHomePage(this.$location);
	}
	
	setDefaultTemplate(log: Log, template?: Template | null) {
		return this.http.patch<unknown>(log._links.self.href, {
			defaultTemplate: template ? template._links.self.href : template ?? null
		})
			.pipe(
				switchMap(res => this.updateLog(log, 'defaultTemplate'))
			);
	}
}